// Plan Q&A Tab Component
// Handles plan-specific Q&A conversations with AI assistant

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Chip,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  Send as SendIcon,
  History as HistoryIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Psychology as TrainingIcon,
  QuestionAnswer as QuestionAnswerIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import PlanContextBanner from './PlanContextBanner';
import {
  PlanQaContext,
  PlanQaMessage
} from '../types/planQa';
import { planQaIntegrationService } from '../services/planQaIntegrationService';
import { planQaAiService } from '../services/planQaAiService';
// Removed unused auth import

interface PlanQaTabProps {
  planContext: PlanQaContext | null;
  onSendMessage: (message: string) => void;
  conversationId: string; // Add conversation ID prop
  user: any; // User object with updated data from Firestore
}

const PlanQaTab: React.FC<PlanQaTabProps> = ({ planContext, onSendMessage, conversationId, user }) => {
  const { t } = useTranslation();

  // Helper function to translate context type
  const getContextTypeLabel = (contextType: string) => {
    switch (contextType) {
      case 'goal':
        return t('goal');
      case 'activity':
        return t('activity');
      case 'tip':
        return t('tip');
      case 'parent_sharing':
        return t('parentSharing');
      default:
        return t('general');
    }
  };

  // State management
  const [planQaMessages, setPlanQaMessages] = useState<PlanQaMessage[]>([]);
  const [conversationIds, setConversationIds] = useState<string[]>([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());


  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Language detection
  const detectLanguage = (text: string) => {
    if (/[\u4e00-\u9fff]/.test(text)) return 'zh-CN';
    if (/[áéíóúñü]/.test(text.toLowerCase())) return 'es';
    if (/[àâäéèêëïîôöùûüÿç]/.test(text.toLowerCase())) return 'fr';
    return 'en';
  };

  const language = planContext ? detectLanguage(planContext.planTitle + planContext.planDescription) : 'en';

  // Multi-language text
  const getText = (key: string) => {
    const texts = {
      'en': {
        noPlanContext: 'No plan context available',
        askQuestion: t('askQuestionAboutPlanElement'),
        send: 'Send',
        loading: 'Getting AI response...',
        conversationHistory: 'Conversation History',
        showHistory: t('showHistory'),
        hideHistory: t('hideHistory'),
        noConversations: 'No conversations yet for this day',
        startConversation: 'Ask your first question about this plan element',
        helpful: 'Was this helpful?',
        rate: 'Rate this response',
        followUp: 'Suggested follow-up questions:',
        error: 'Error',
        tryAgain: 'Try Again'
      },
      'zh-CN': {
        noPlanContext: '没有计划上下文可用',
        askQuestion: '询问有关此计划元素的问题...',
        send: '发送',
        loading: '正在获取AI回复...',
        conversationHistory: '对话历史',
        showHistory: '显示历史',
        hideHistory: '隐藏历史',
        noConversations: '今天还没有对话',
        startConversation: '询问您关于此计划元素的第一个问题',
        helpful: '这有帮助吗？',
        rate: '评价此回复',
        followUp: '建议的后续问题：',
        error: '错误',
        tryAgain: '重试'
      },
      'es': {
        noPlanContext: 'No hay contexto de plan disponible',
        askQuestion: 'Haz una pregunta sobre este elemento del plan...',
        send: 'Enviar',
        loading: 'Obteniendo respuesta de IA...',
        conversationHistory: 'Historial de Conversación',
        showHistory: 'Mostrar Historial',
        hideHistory: 'Ocultar Historial',
        noConversations: 'Aún no hay conversaciones para este día',
        startConversation: 'Haz tu primera pregunta sobre este elemento del plan',
        helpful: '¿Fue esto útil?',
        rate: 'Califica esta respuesta',
        followUp: 'Preguntas de seguimiento sugeridas:',
        error: 'Error',
        tryAgain: 'Intentar de Nuevo'
      },
      'fr': {
        noPlanContext: 'Aucun contexte de plan disponible',
        askQuestion: 'Posez une question sur cet élément du plan...',
        send: 'Envoyer',
        loading: 'Obtention de la réponse IA...',
        conversationHistory: 'Historique des Conversations',
        showHistory: 'Afficher l\'Historique',
        hideHistory: 'Masquer l\'Historique',
        noConversations: 'Aucune conversation pour ce jour',
        startConversation: 'Posez votre première question sur cet élément du plan',
        helpful: 'Était-ce utile ?',
        rate: 'Évaluez cette réponse',
        followUp: 'Questions de suivi suggérées :',
        error: 'Erreur',
        tryAgain: 'Réessayer'
      }
    };

    return texts[language as keyof typeof texts]?.[key as keyof typeof texts['en']] ||
      texts['en'][key as keyof typeof texts['en']];
  };

  // Load Plan Q&A messages when plan context changes
  useEffect(() => {
    console.log('🔄 PlanQaTab useEffect triggered:', {
      planContext,
      conversationId,
      hasPlanContext: !!planContext,
      hasPlanId: !!planContext?.planId,
      hasDay: !!planContext?.day
    });

    if (planContext) {
      console.log('📞 Calling loadPlanQaMessages with:', {
        planId: planContext.planId,
        day: planContext.day
      });
      loadPlanQaMessages(planContext.planId, planContext.day);
    } else {
      console.log('❌ No planContext available, not loading messages');
    }
  }, [planContext]);

  // Scroll to bottom when messages change
  useEffect(() => {
    console.log('📜 planQaMessages state updated:', {
      messageCount: planQaMessages.length,
      messages: planQaMessages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content?.substring(0, 30) + '...',
        planContext: msg.planContext
      }))
    });

    // Auto-expand all days when messages are loaded
    if (planQaMessages.length > 0) {
      const { sortedDays } = organizeMessagesByDay(planQaMessages);
      const newExpandedDays = new Set(sortedDays);
      setExpandedDays(newExpandedDays);
      console.log('🔓 Auto-expanding all days:', Array.from(newExpandedDays));
    }

    scrollToBottom();
  }, [planQaMessages]);

  // Auto-focus input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [planContext]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Organize messages by day
  const organizeMessagesByDay = (messages: PlanQaMessage[]) => {
    const messagesByDay: { [day: number]: PlanQaMessage[] } = {};

    messages.forEach(message => {
      const day = message.planContext?.day || 1;
      if (!messagesByDay[day]) {
        messagesByDay[day] = [];
      }
      messagesByDay[day].push(message);
    });

    // Sort days in ascending order
    const sortedDays = Object.keys(messagesByDay)
      .map(Number)
      .sort((a, b) => a - b);

    return { messagesByDay, sortedDays };
  };

  // Toggle day expansion
  const toggleDayExpansion = (day: number) => {
    const newExpandedDays = new Set(expandedDays);
    if (newExpandedDays.has(day)) {
      newExpandedDays.delete(day);
    } else {
      newExpandedDays.add(day);
    }
    setExpandedDays(newExpandedDays);
  };

  const loadPlanQaMessages = async (planId: string, day: number) => {
    try {
      setError(null);

      console.log('🔄 Starting to load Plan Q&A messages:', {
        planId,
        day,
        conversationId,
        planContext
      });

      // Load Plan Q&A messages from the current conversation for the entire plan (all days)
      // Remove the day filter to get all Plan Q&A messages from this conversation
      try {
        if (conversationId) {
          console.log('📞 Calling getPlanQaMessages with:', { conversationId, planId });
          const messages = await planQaIntegrationService.getPlanQaMessages(conversationId, planId);

          console.log('📥 Received messages from service:', {
            messageCount: messages.length,
            messages: messages.map(msg => ({
              id: msg.id,
              role: msg.role,
              content: msg.content?.substring(0, 50) + '...',
              planContext: msg.planContext
            }))
          });

          setPlanQaMessages(messages);
          setConversationIds([conversationId]);

          console.log('✅ Loaded Plan Q&A messages from current conversation:', {
            totalMessages: messages.length,
            conversations: 1,
            messagesByDay: organizeMessagesByDay(messages)
          });
        } else {
          console.log('❌ No conversationId available');
          setConversationIds([]);
          setPlanQaMessages([]);
        }
      } catch (queryError) {
        // If the query fails (e.g., missing index), just start with empty state
        console.log('❌ Query failed, starting with empty Plan Q&A state:', queryError);
        setConversationIds([]);
        setPlanQaMessages([]);
        // Don't show error to user for this case
      }
    } catch (error) {
      console.error('❌ Error loading Plan Q&A messages:', error);
      setError('Failed to load Plan Q&A messages');
    }
  };




  const handleNewQuestion = async (question: string) => {
    if (!planContext || !question.trim()) return;

    // Check authentication before proceeding
    if (!user) {
      setError('User not authenticated. Please refresh the page and try again.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use the conversation ID from props
      const currentConversationId = conversationId;

      // Add user message to the existing conversation
      await planQaIntegrationService.addPlanQaMessage(
        currentConversationId,
        {
          content: question,
          role: 'user',
          planContext: {
            planId: planContext.planId,
            day: planContext.day,
            contextType: planContext.contextType,
            contextElement: planContext.contextElement,
            conversationFlow: 'question'
          }
        }
      );

      // Get AI response
      const aiResponse = await planQaAiService.generatePlanQaResponse(
        question,
        planContext,
        planQaMessages
      );

      // Add AI message to the existing conversation
      await planQaIntegrationService.addPlanQaMessage(
        currentConversationId,
        {
          content: aiResponse.content,
          role: 'assistant',
          planContext: {
            planId: planContext.planId,
            day: planContext.day,
            contextType: planContext.contextType,
            contextElement: planContext.contextElement,
            conversationFlow: 'answer'
          },
          tokensUsed: aiResponse.tokensUsed,
          responseTime: aiResponse.responseTime
        }
      );

      // Add the new messages to the local state immediately
      const newUserMessage: PlanQaMessage = {
        id: `temp-${Date.now()}-user`,
        content: question,
        role: 'user',
        createdAt: new Date() as any,
        conversationId: currentConversationId,
        userId: user?.uid || 'unknown',
        planContext: {
          planId: planContext.planId,
          day: planContext.day,
          contextType: planContext.contextType,
          contextElement: planContext.contextElement,
          conversationFlow: 'question'
        }
      };

      const newAiMessage: PlanQaMessage = {
        id: `temp-${Date.now()}-ai`,
        content: aiResponse.content,
        role: 'assistant',
        createdAt: new Date() as any,
        conversationId: currentConversationId,
        userId: 'assistant',
        planContext: {
          planId: planContext.planId,
          day: planContext.day,
          contextType: planContext.contextType,
          contextElement: planContext.contextElement,
          conversationFlow: 'answer'
        },
        tokensUsed: aiResponse.tokensUsed,
        responseTime: aiResponse.responseTime
      };

      // Update local state with new messages
      setPlanQaMessages(prev => [...prev, newUserMessage, newAiMessage]);

      // Clear input
      setNewQuestion('');

      // Trigger parent callback
      onSendMessage(question);
    } catch (error) {
      console.error('Error handling new question:', error);

      // More detailed error logging
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });

        if (error.message.includes('API key')) {
          setError('OpenAI API key not configured. Please check your environment settings.');
        } else if (error.message.includes('Assistant ID')) {
          setError('OpenAI Assistant ID not configured. Please check your environment settings.');
        } else if (error.message.includes('rate limit')) {
          setError('Rate limit exceeded. Please try again later.');
        } else if (error.message.includes('permission') || error.message.includes('Missing or insufficient permissions')) {
          setError('Firebase permission error. Please refresh the page and ensure you are logged in.');
        } else if (error.message.includes('not authenticated') || error.message.includes('authentication')) {
          setError('Authentication error. Please refresh the page and log in again.');
        } else {
          setError(`AI Service Error: ${error.message}`);
        }
      } else {
        setError('Failed to get AI response. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };



  const handleRateResponse = async (messageId: string, rating: number, isHelpful: boolean) => {
    // TODO: Implement message rating functionality
    console.log('Rating response:', { messageId, rating, isHelpful });
  };

  // Check if user is authenticated
  if (!user) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography variant="h6" color="text.secondary">
          {language === 'zh-CN' ? '请先登录以使用问答功能' : 'Please sign in to use the Q&A feature'}
        </Typography>
        <Button variant="contained" color="primary">
          {language === 'zh-CN' ? '登录' : 'Sign In'}
        </Button>
      </Box>
    );
  }

  if (!planContext) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        p: 3
      }}>
        <TrainingIcon sx={{ fontSize: 64, color: 'primary.main', opacity: 0.5 }} />
        <Typography variant="h6" color="text.secondary" textAlign="center">
          {getText('noPlanContext')}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 300 }}>
          {language === 'zh-CN'
            ? '点击右侧路线图中的活动旁边的问答图标来开始针对特定活动的问答对话。'
            : language === 'es'
              ? 'Haz clic en el ícono de pregunta y respuesta junto a las actividades en el mapa de ruta de la derecha para comenzar conversaciones específicas sobre actividades.'
              : language === 'fr'
                ? 'Cliquez sur l\'icône de question-réponse à côté des activités dans la feuille de route de droite pour commencer des conversations spécifiques sur les activités.'
                : 'Click the question and answer icon next to activities in the roadmap on the right to start activity-specific Q&A conversations.'
          }
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Plan Context Banner */}
      <PlanContextBanner {...planContext} />

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setError(null)}>
              {getText('tryAgain')}
            </Button>
          }
        >
          {getText('error')}: {error}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Conversation History Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<HistoryIcon />}
            endIcon={showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowHistory(!showHistory)}
            variant="outlined"
            size="small"
          >
            {showHistory ? getText('hideHistory') : getText('showHistory')}
            ({planQaMessages.length > 0 ? organizeMessagesByDay(planQaMessages).sortedDays.length : 0})
          </Button>
        </Box>

        {/* Plan Q&A History - Organized by Day */}
        <Collapse in={showHistory}>
          <Paper variant="outlined" sx={{ mb: 2, maxHeight: 150, overflow: 'auto' }}>
            {(() => {
              console.log('🎨 Rendering history section:', {
                showHistory,
                planQaMessagesLength: planQaMessages.length,
                conversationIdsLength: conversationIds.length,
                planQaMessages: planQaMessages.map(msg => ({
                  id: msg.id,
                  role: msg.role,
                  planContext: msg.planContext
                }))
              });
              return null;
            })()}
            {planQaMessages.length > 0 ? (
              <Box sx={{ p: 1 }}>
                {(() => {
                  const { messagesByDay, sortedDays } = organizeMessagesByDay(planQaMessages);
                  return sortedDays.map(day => (
                    <Box key={day} sx={{ mb: 1 }}>
                      {/* Day Header */}
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={expandedDays.has(day) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        onClick={() => toggleDayExpansion(day)}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          fontWeight: 500,
                          mb: 1
                        }}
                      >
                        {`${t('day')} ${day}${t('daySuffix')} (${messagesByDay[day].length} ${messagesByDay[day].length === 1 ? t('conversation') : t('conversations')})`}
                      </Button>

                      {/* Day Content */}
                      <Collapse in={expandedDays.has(day)}>
                        <Box sx={{ pl: 2, pr: 1 }}>
                          <List dense>
                            {messagesByDay[day].map((message, index) => (
                              <React.Fragment key={message.id}>
                                <ListItem
                                  sx={{
                                    cursor: 'default',
                                    backgroundColor: 'transparent',
                                    py: 0.5
                                  }}
                                >
                                  <ListItemText
                                    primary={message.content.substring(0, 60) + (message.content.length > 60 ? '...' : '')}
                                    secondary={`${message.createdAt instanceof Date
                                      ? message.createdAt.toLocaleTimeString()
                                      : new Date(message.createdAt.toDate()).toLocaleTimeString()
                                      } • ${getContextTypeLabel(message.planContext?.contextType || 'general')}`}
                                    primaryTypographyProps={{
                                      noWrap: true,
                                      fontSize: '0.85rem'
                                    }}
                                    secondaryTypographyProps={{
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                  {message.userRating && (
                                    <Chip
                                      icon={<StarIcon />}
                                      label={message.userRating}
                                      size="small"
                                      variant="outlined"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </ListItem>
                                {index < messagesByDay[day].length - 1 && <Divider />}
                              </React.Fragment>
                            ))}
                          </List>
                        </Box>
                      </Collapse>
                    </Box>
                  ));
                })()}
              </Box>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {getText('noConversations')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Collapse>

        {/* Messages Display */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            overflow: 'auto',
            mb: 2,
            p: 1
          }}
        >
          {planQaMessages.length > 0 ? (
            <Box sx={{ p: 1 }}>
              {planQaMessages.map((message, index) => (
                <Box
                  key={message.id}
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      maxWidth: '90%',
                      backgroundColor: message.role === 'user'
                        ? 'primary.light'
                        : 'background.default',
                      color: message.role === 'user'
                        ? 'primary.contrastText'
                        : 'text.primary'
                    }}
                  >
                    {message.role === 'assistant' ? (
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <Typography variant="body1" sx={{ mb: 1 }}>{children}</Typography>,
                          strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
                          em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                          h1: ({ children }) => <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>{children}</Typography>,
                          h2: ({ children }) => <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>{children}</Typography>,
                          h3: ({ children }) => <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>{children}</Typography>,
                          ul: ({ children }) => <Box component="ul" sx={{ pl: 2, mb: 1 }}>{children}</Box>,
                          ol: ({ children }) => <Box component="ol" sx={{ pl: 2, mb: 1 }}>{children}</Box>,
                          li: ({ children }) => <Box component="li" sx={{ mb: 0.5 }}>{children}</Box>,
                          blockquote: ({ children }) => (
                            <Box sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 2, ml: 1, mb: 1, fontStyle: 'italic' }}>
                              {children}
                            </Box>
                          ),
                          code: ({ children }) => (
                            <Box component="code" sx={{
                              backgroundColor: 'grey.100',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 0.5,
                              fontFamily: 'monospace',
                              fontSize: '0.875rem'
                            }}>
                              {children}
                            </Box>
                          ),
                          pre: ({ children }) => (
                            <Box component="pre" sx={{
                              backgroundColor: 'grey.100',
                              p: 1,
                              borderRadius: 1,
                              overflow: 'auto',
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              mb: 1
                            }}>
                              {children}
                            </Box>
                          )
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    ) : (
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.content}
                      </Typography>
                    )}

                    {/* Message metadata */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 1
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        {message.createdAt instanceof Date
                          ? message.createdAt.toLocaleTimeString()
                          : new Date(message.createdAt.toDate()).toLocaleTimeString()
                        }
                      </Typography>

                      {/* Rating buttons for AI messages */}
                      {message.role === 'assistant' && (
                        <Box sx={{ display: 'flex', 'gap': 0.5 }}>
                          <Tooltip title={getText('helpful')}>
                            <IconButton
                              size="small"
                              onClick={() => handleRateResponse(message.id, 5, true)}
                            >
                              <ThumbUpIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={getText('rate')}>
                            <IconButton
                              size="small"
                              onClick={() => handleRateResponse(message.id, 2, false)}
                            >
                              <ThumbDownIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </Box>
          ) : (
            <Box sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4
            }}>
              <QuestionAnswerIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {getText('startConversation')}
              </Typography>
            </Box>
          )}
        </Paper>



        {/* Message Input */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={3}
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            placeholder={getText('askQuestion')}
            disabled={isLoading}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleNewQuestion(newQuestion);
              }
            }}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            onClick={() => handleNewQuestion(newQuestion)}
            disabled={!newQuestion.trim() || isLoading}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <SendIcon />
            )}
          </Button>
        </Box>

        {/* Loading indicator */}
        {isLoading && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 1
          }}>
            <Typography variant="body2" color="text.secondary">
              {getText('loading')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PlanQaTab;
